// Zustand auth slice — TENANT staff session.
//
// Holds the in-memory representation of the current tenant user, the
// URL_PARAM_SECRET (which never touches storage), and a derived
// isAuthenticated flag. Tokens themselves live in the storage abstraction
// (utils/storage.ts) so they survive a page refresh.
//
// Status lifecycle:
//   loading      — boot sequence in flight (reading tokens, calling /me)
//   authenticated — /me returned a user
//   anonymous    — no tokens, or /me failed and refresh failed too
//
// Wave I — platform super-admin sessions live in `superAdminStore`, NOT
// here. Impersonation state (banner / stashed token) also moved there.
// During impersonation `authStore.user` is the impersonated tenant admin
// (populated from /me using the impersonation token).

import { create } from 'zustand';
import { storage } from '../utils/storage';
import type { UserSummary } from '../types/api';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthState {
  status: AuthStatus;
  user: UserSummary | null;
  urlParamSecret: string | null;
  isAuthenticated: boolean;
}

export interface AuthActions {
  setLoading: () => void;
  setAuthenticated: (user: UserSummary, urlParamSecret: string) => void;
  setAnonymous: () => void;
  updateUser: (patch: Partial<UserSummary>) => void;
  reset: () => void;
}

export type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  status: 'loading',
  user: null,
  urlParamSecret: null,
  isAuthenticated: false,
};

export const useAuthStore = create<AuthStore>((set) => ({
  ...initialState,

  setLoading: () => set({ status: 'loading' }),

  setAuthenticated: (user, urlParamSecret) =>
    set({
      status: 'authenticated',
      user,
      urlParamSecret,
      isAuthenticated: true,
    }),

  setAnonymous: () =>
    set({
      status: 'anonymous',
      user: null,
      urlParamSecret: null,
      isAuthenticated: false,
    }),

  updateUser: (patch) =>
    set((state) => (state.user ? { user: { ...state.user, ...patch } } : state)),

  reset: () => set({ ...initialState, status: 'anonymous', isAuthenticated: false }),
}));

// One-time cleanup of pre-typed-wrapper localStorage keys (tricivo_token,
// tricivo_user). Idempotent and side-effecting at module load — runs once
// per page load when this store is imported. Mirrored in
// `superAdminStore`.
storage.removeLegacyKeys();
